'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { HelpCircleIcon, Remove01Icon, Add01Icon } from '@terra/ui-icons';
import { Spinner } from '../../../components/spinner';
import {
  type AskUserMessage,
  type MentionEntity,
  type MessageSegment,
  type PromptOption,
  ASK_USER_SKIP_SENTINEL,
} from '@terra/shared-dto-chat-types';
import { Button } from '../../../components/styled/button';
import { IconButton } from '../../../components/icon-button';
import { Text } from '../../../components/styled/text';
import { Input } from '../../../components/styled/input';
import { InlineMessageContent } from './InlineMessageContent';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'] as const;
const CUSTOM_VALUE = '__custom__';
const SKIP_OPTION: PromptOption = {
  value: ASK_USER_SKIP_SENTINEL,
  label: ASK_USER_SKIP_SENTINEL,
};

type AskUserPromptCardProps = {
  dataMessage?: AskUserMessage;
  selectedValue?: string;
  answerMentions?: MentionEntity[];
  answerSegments?: MessageSegment[];
  onDataMessageEvent?: (event: {
    dataMessage: AskUserMessage;
    selectedOption: PromptOption;
  }) => void;
};

export function AskUserPromptCard({
  dataMessage,
  selectedValue,
  answerMentions,
  answerSegments,
  onDataMessageEvent,
}: AskUserPromptCardProps) {
  const t = useTranslations('designSystem.chat.askUserPromptCard');
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);
  const [customText, setCustomText] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const askUserPrompt = dataMessage?.askUserPrompt;
  const mentionEntitiesById = useMemo(() => {
    if (!answerMentions?.length) {
      return undefined;
    }

    return new Map(
      answerMentions.map((mention) => [mention.entityId, mention]),
    );
  }, [answerMentions]);

  useEffect(() => {
    if (selectedValue !== undefined && selectedValue !== null) {
      setIsSubmitting(false);
    }
  }, [selectedValue]);

  if (!askUserPrompt) {
    return null;
  }

  const isAnswered = selectedValue !== undefined && selectedValue !== null;
  const isSkipped = isAnswered && selectedValue === ASK_USER_SKIP_SENTINEL;
  const answeredOption = isAnswered
    ? askUserPrompt.options.find(
        (option) =>
          option.value === selectedValue || option.label === selectedValue,
      )
    : undefined;
  const isCustomAnswer = isAnswered && !isSkipped && !answeredOption;
  const showMentionAnswer =
    isCustomAnswer && !!answerSegments?.length && !!mentionEntitiesById?.size;
  const isCustomPending = pendingSelection === CUSTOM_VALUE;
  const canContinue =
    pendingSelection !== null &&
    (pendingSelection !== CUSTOM_VALUE || customText.trim().length > 0);
  const canMinimize = !isAnswered;
  const showBody = !canMinimize || !isMinimized;
  const minimizeLabel = isMinimized ? t('expand') : t('minimize');

  const submit = (option: PromptOption) => {
    if (!dataMessage || submittedRef.current) return;
    submittedRef.current = true;
    setIsSubmitting(true);
    onDataMessageEvent?.({ dataMessage, selectedOption: option });
  };

  const handleContinue = () => {
    if (!canContinue) return;
    if (isCustomPending) {
      submit({ value: customText.trim(), label: customText.trim() });
    } else {
      const selected = askUserPrompt.options.find(
        (o) => o.value === pendingSelection,
      );

      if (selected) submit(selected);
    }
  };

  const toggleOption = (value: string) => {
    setPendingSelection(pendingSelection === value ? null : value);
    if (value !== CUSTOM_VALUE) setCustomText('');
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="lg"
      overflow="hidden"
      width="100%"
    >
      <HStack
        px={2.5}
        py={1}
        bg="bg.subtle"
        borderBottomWidth={showBody || !isAnswered ? '1px' : '0'}
        borderBottomColor="border.neutral.muted"
        justifyContent="space-between"
      >
        <HStack gap={1}>
          <Box color="fg.muted">
            <HelpCircleIcon size={14} />
          </Box>
          <Text fontSize="xs" color="fg.muted">
            {t('header')}
          </Text>
        </HStack>
        {canMinimize ? (
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={minimizeLabel}
            onClick={() => setIsMinimized((prev) => !prev)}
            color="fg.muted"
            _hover={{ color: 'fg.default', bg: 'transparent' }}
          >
            {isMinimized ? <Add01Icon size={14} /> : <Remove01Icon size={14} />}
          </IconButton>
        ) : null}
      </HStack>

      {showBody ? (
        <VStack alignItems="start" gap={0} px={2.5} pt={2} pb={1.5}>
          <Text fontSize="sm" fontWeight="semibold" color="fg.default" mb={1.5}>
            {askUserPrompt.prompt}
          </Text>

          <VStack alignItems="stretch" gap={0} width="100%">
            {askUserPrompt.options.map((option, index) => {
              const letter = OPTION_LETTERS[index];
              const isSelectedInAnswer =
                isAnswered &&
                (option.value === selectedValue ||
                  option.label === selectedValue);
              const isPending =
                !isAnswered && pendingSelection === option.value;
              const isInteractive = !isAnswered && !isSubmitting;

              return (
                <Box
                  key={option.value}
                  as={isInteractive ? 'button' : 'div'}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  px={1.5}
                  py={1}
                  borderRadius="sm"
                  cursor={isInteractive ? 'pointer' : 'default'}
                  bg={
                    isSelectedInAnswer || isPending
                      ? 'bg.accent.subtle'
                      : 'transparent'
                  }
                  color={
                    isSelectedInAnswer || isPending ? 'fg.accent' : 'fg.default'
                  }
                  opacity={
                    (isAnswered && !isSelectedInAnswer) ||
                    (isSubmitting && !isPending)
                      ? 0.45
                      : 1
                  }
                  _hover={
                    isInteractive ? { bg: 'bg.neutral.subtle' } : undefined
                  }
                  transition="background 0.1s, opacity 0.15s"
                  onClick={
                    isInteractive ? () => toggleOption(option.value) : undefined
                  }
                  textAlign="left"
                  width="100%"
                >
                  <OptionBadge
                    letter={letter}
                    active={isSelectedInAnswer || isPending}
                  />
                  <Text fontSize="xs" color="inherit" lineHeight="snug">
                    {option.label}
                  </Text>
                </Box>
              );
            })}

            {!isAnswered && (
              <Box
                as={isCustomPending ? 'div' : 'button'}
                display="flex"
                alignItems="center"
                gap={2}
                px={1.5}
                py={1}
                borderRadius="sm"
                cursor={isCustomPending ? 'text' : 'pointer'}
                bg={isCustomPending ? 'bg.accent.subtle' : 'transparent'}
                color={isCustomPending ? 'fg.accent' : 'fg.muted'}
                _hover={
                  isCustomPending ? undefined : { bg: 'bg.neutral.subtle' }
                }
                transition="background 0.1s"
                onClick={
                  isCustomPending ? undefined : () => toggleOption(CUSTOM_VALUE)
                }
                textAlign="left"
                width="100%"
              >
                <OptionBadge
                  letter={OPTION_LETTERS[askUserPrompt.options.length]}
                  active={isCustomPending}
                />
                {isCustomPending ? (
                  <Input
                    autoFocus
                    flex={1}
                    minW={0}
                    value={customText}
                    onChange={(event) => setCustomText(event.target.value)}
                    placeholder={t('somethingElsePlaceholder')}
                    fontSize="xs"
                    bg="transparent"
                    borderWidth="0"
                    outline="none"
                    color="inherit"
                    px={0}
                    h="auto"
                    _focus={{ outline: 'none', boxShadow: 'none' }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleContinue();
                      }
                    }}
                  />
                ) : (
                  <Text fontSize="xs" color="inherit" lineHeight="snug">
                    {t('somethingElse')}
                  </Text>
                )}
              </Box>
            )}

            {isAnswered && isCustomAnswer && (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                px={1.5}
                py={1}
                borderRadius="sm"
                bg="bg.accent.subtle"
                color="fg.accent"
              >
                <OptionBadge
                  letter={OPTION_LETTERS[askUserPrompt.options.length]}
                  active
                />
                {showMentionAnswer && answerSegments ? (
                  <Box flex={1} minW={0}>
                    <InlineMessageContent
                      segments={answerSegments}
                      mentionEntitiesById={mentionEntitiesById}
                    />
                  </Box>
                ) : (
                  <Text fontSize="xs" color="inherit" lineHeight="snug">
                    {selectedValue}
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </VStack>
      ) : null}

      <Divider />

      {isAnswered ? (
        <HStack
          px={2.5}
          py={1}
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
        >
          {isSkipped ? (
            <Text fontSize="xs" color="fg.muted">
              {t('skipped')}
            </Text>
          ) : showMentionAnswer && answerSegments ? (
            <>
              <Box flex={1} minW={0} display="flex" justifyContent="flex-end">
                <InlineMessageContent
                  segments={answerSegments}
                  mentionEntitiesById={mentionEntitiesById}
                />
              </Box>
              <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                — {t('selected')}
              </Text>
            </>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              {`${answeredOption?.label ?? selectedValue} — ${t('selected')}`}
            </Text>
          )}
        </HStack>
      ) : isSubmitting ? (
        <HStack px={2.5} py={1} justifyContent="flex-end" gap={2}>
          <Spinner size="xs" />
          <Text fontSize="2xs" color="fg.muted">
            {t('continue')}...
          </Text>
        </HStack>
      ) : (
        <HStack px={2.5} py={1} justifyContent="flex-end" gap={2}>
          <Button
            variant="ghost"
            size="xs"
            color="fg.muted"
            onClick={() => submit(SKIP_OPTION)}
          >
            {t('skip')}
          </Button>
          <Button
            variant="outline"
            size="xs"
            disabled={!canContinue}
            onClick={handleContinue}
          >
            {t('continue')}
          </Button>
        </HStack>
      )}
    </Box>
  );
}

function OptionBadge({ letter, active }: { letter: string; active: boolean }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w={4}
      h={4}
      borderRadius="xs"
      borderWidth="1px"
      borderColor={active ? 'border.accent' : 'border.neutral.default'}
      bg={active ? 'bg.accent.muted' : 'transparent'}
    >
      <Text
        fontSize="2xs"
        fontWeight="bold"
        color={active ? 'fg.accent' : 'fg.muted'}
        lineHeight={1}
      >
        {letter}
      </Text>
    </Box>
  );
}
